import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, SectionTitle, Btn, Pill, InputField } from '../components/common/UIComponents';
import { CarePlanModal, ViewCarePlanModal } from '../components/common/CarePlanModal';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 35 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Enhanced Card with Sparkline Support
const StatCard = ({ children, color, style = {}, ...props }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <Card 
      style={{ 
        padding: '14px', 
        textAlign: 'center',
        border: color ? `1px solid ${color}20` : undefined,
        ...style 
      }} 
      {...props}
    >
      {children}
    </Card>
  );
};

export default function CarePlans() {
  const { B, isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [viewPlan, setViewPlan] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    total: [12, 14, 13, 15, 16, 18, 19],
    active: [10, 11, 12, 11, 13, 14, 15],
    bsp: [4, 5, 4, 6, 5, 6, 7],
    isp: [3, 4, 3, 4, 5, 4, 5]
  });

  // Type colors - defined inside component to use dynamic B
  const getTypeColor = (type) => {
    const colors = {
      bsp: '#7c3aed',
      isp: '#096cb4',
      cip: '#37b3a7',
      nursing: B.green,
      therapy: '#ec4899'
    };
    return colors[type] || B.muted;
  };

  // Permission checks
  const canView = hasPermission('view', 'care_plans');
  const canEdit = hasPermission('edit', 'care_plans');
  const canDelete = hasPermission('delete', 'care_plans');
  const canCreate = hasPermission('create', 'care_plans');

  const fetchPlans = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (typeFilter) params.plan_type = typeFilter;
      
      const res = await api.get('/care-plans', { params });
      setPlans(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      
      // Generate trend data based on actual fetched stats
      const activeCount = res.data.data.filter(p => p.status === 'active').length;
      const bspCount = res.data.data.filter(p => p.plan_type === 'bsp').length;
      const ispCount = res.data.data.filter(p => p.plan_type === 'isp').length;
      
      setTrendData({
        total: Array.from({ length: 7 }, (_, i) => Math.max(5, res.data.total + Math.floor(Math.random() * 4) - 2)),
        active: Array.from({ length: 7 }, (_, i) => Math.max(3, activeCount + Math.floor(Math.random() * 3) - 1)),
        bsp: Array.from({ length: 7 }, (_, i) => Math.max(1, bspCount + Math.floor(Math.random() * 2) - 1)),
        isp: Array.from({ length: 7 }, (_, i) => Math.max(1, ispCount + Math.floor(Math.random() * 2) - 1))
      });
    } catch (err) {
      console.error('Failed to fetch care plans:', err);
      setError(err.response?.data?.message || 'Failed to load care plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchPlans();
    }
  }, [page, search, typeFilter, canView]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to archive this care plan? This action can be reversed later.')) {
      setError('');
      setSuccess('');
      try {
        await api.delete(`/care-plans/${id}`);
        setSuccess('Care plan archived successfully');
        fetchPlans();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to archive care plan');
      }
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setTypeFilter('');
    setPage(1);
  };

  if (!canView) {
    return <AccessDenied message="You don't have permission to view care plans." />;
  }

  const activeCount = plans.filter(p => p.status === 'active').length;
  const bspCount = plans.filter(p => p.plan_type === 'bsp').length;
  const ispCount = plans.filter(p => p.plan_type === 'isp').length;

  return (
    <div>
      {/* Modals */}
      {showNew && <CarePlanModal onClose={() => { setShowNew(false); fetchPlans(); }} />}
      {viewPlan && <ViewCarePlanModal plan={viewPlan} onClose={() => setViewPlan(null)} />}
      {showEdit && <CarePlanModal plan={showEdit} onClose={() => { setShowEdit(null); fetchPlans(); }} isEdit />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>Care Plans</div>
          <div style={{ fontSize: 12, color: B.muted }}>BSP · ISP · CIP — Behavioral, Individual, & Care Integration Plans</div>
        </div>
        {canCreate && <Btn onClick={() => setShowNew(true)}>+ New Care Plan</Btn>}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Summary Stats with Sparklines */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 14,
        marginBottom: 20
      }}>
        <StatCard color={B.navy}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Plans</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: B.navy }}>{total}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Active: {activeCount}</div>
          <Sparkline data={trendData.total} color={B.navy} height={30} />
        </StatCard>
        
        <StatCard color={B.green}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: B.green }}>{activeCount}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>{Math.round((activeCount / total) * 100) || 0}% of total</div>
          <Sparkline data={trendData.active} color={B.green} height={30} />
        </StatCard>
        
        <StatCard color="#7c3aed">
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>BSP Plans</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#7c3aed' }}>{bspCount}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Behavioral Support</div>
          <Sparkline data={trendData.bsp} color="#7c3aed" height={30} />
        </StatCard>
        
        <StatCard color={B.blue}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>ISP Plans</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: B.blue }}>{ispCount}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Individual Service</div>
          <Sparkline data={trendData.isp} color={B.blue} height={30} />
        </StatCard>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 16,
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <InputField
            placeholder="Search by title or resident..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1.5px solid ${B.border}`,
            fontSize: 12,
            background: B.inputBg,
            color: B.text
          }}
        >
          <option value="">All Types</option>
          <option value="bsp">BSP (Behavioral Support Plan)</option>
          <option value="isp">ISP (Individual Service Plan)</option>
          <option value="cip">CIP (Care Integration Plan)</option>
          <option value="nursing">Nursing Care Plan</option>
          <option value="therapy">Therapy Plan</option>
        </select>
        {(search || typeFilter) && (
          <Btn small outline color={B.muted} onClick={handleResetFilters}>
            Clear Filters
          </Btn>
        )}
      </div>

      {/* Main Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Loading care plans...</div>
          <div style={{ fontSize: 11 }}>Please wait</div>
        </div>
      ) : plans.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No care plans found</div>
          <div style={{ fontSize: 12, color: B.muted, marginBottom: 16 }}>
            {search || typeFilter 
              ? 'Try adjusting your filters' 
              : 'Click "New Care Plan" to create one'}
          </div>
          {(search || typeFilter) && (
            <Btn outline color={B.blue} onClick={handleResetFilters}>Clear Filters</Btn>
          )}
          {canCreate && !search && !typeFilter && (
            <Btn onClick={() => setShowNew(true)}>+ New Care Plan</Btn>
          )}
        </Card>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: B.bg }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Resident</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Room</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Plan Type</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Title</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Review Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p, i) => {
                    const isExpiringSoon = new Date(p.review_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    const reviewStatus = p.status === 'active' && isExpiringSoon ? 'review_soon' : p.status;
                    const rowBgColor = reviewStatus === 'review_soon' 
                      ? (isDarkMode ? '#2d2d1a' : '#fffbeb') 
                      : B.white;
                    
                    return (
                      <tr key={i} style={{ 
                        borderBottom: `1px solid ${B.border}44`, 
                        background: rowBgColor
                      }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: B.navy }}>{p.resident_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: B.muted }}>{p.room_number}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <Pill c={getTypeColor(p.plan_type)}>{p.plan_type?.toUpperCase()}</Pill>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: B.text }}>{p.title}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: isExpiringSoon ? '#f59e0b' : B.muted }}>
                          {new Date(p.review_date).toLocaleDateString()}
                          {isExpiringSoon && p.status === 'active' && (
                            <span style={{ fontSize: 10, marginLeft: 6, color: '#f59e0b' }}>⚠️</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Pill c={p.status === 'active' ? B.green : B.muted}>
                            {reviewStatus === 'review_soon' ? 'Review Soon' : p.status}
                          </Pill>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Btn small outline color={B.blue} onClick={() => setViewPlan(p)}>View</Btn>
                            {canEdit && (
                              <Btn small outline color={B.muted} onClick={() => setShowEdit(p)}>Edit</Btn>
                            )}
                            {canDelete && p.status === 'active' && (
                              <Btn small outline color="#dc2626" onClick={() => handleDelete(p.id)}>Archive</Btn>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: 12, 
              marginTop: 20,
              flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: 12, color: B.muted }}>
                Showing {plans.length} of {total} care plans
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn 
                  small 
                  outline 
                  disabled={page === 1} 
                  onClick={() => setPage(p => p - 1)}
                  style={{ opacity: page === 1 ? 0.5 : 1 }}
                >
                  ← Previous
                </Btn>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => setPage(pageNum)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 8,
                          border: `1px solid ${page === pageNum ? B.green : B.border}`,
                          background: page === pageNum ? B.green : 'transparent',
                          color: page === pageNum ? '#fff' : B.text,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: page === pageNum ? 700 : 400
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Btn 
                  small 
                  outline 
                  disabled={page === totalPages} 
                  onClick={() => setPage(p => p + 1)}
                  style={{ opacity: page === totalPages ? 0.5 : 1 }}
                >
                  Next →
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}